/**
 * ActionCardsSection Component
 *
 * Container for the Eligible Complaints and Vulnerability Detector cards.
 * Renders side-by-side in a 2-column grid on desktop, stacked on mobile.
 *
 * Atomic level: Organism
 * Composes: ActionCard, RecordRow, VulnerabilityRecordRow
 * Used by: TodayPage
 */

import React from 'react';
import { ActionCard } from './ActionCard';
import { RecordRow } from './RecordRow';
import { VulnerabilityRecordRow } from './VulnerabilityRecordRow';
import type { ActionCardsSectionProps } from '../types/today.types';

/**
 * Container for the Eligible Complaints and Vulnerability Detector cards.
 * Renders side-by-side in a 2-column grid on desktop, stacked on mobile.
 *
 * Responsive: Single column on mobile, 2 columns on desktop (lg+)
 */
export function ActionCardsSection({
  eligibleComplaints,
  vulnerabilityDetector,
}: ActionCardsSectionProps): React.ReactElement {
  // Individual record clicks → v4 record detail with proper collection context (opens in new tab)
  const handleRecordClick = (id: string, collectionId: string) => {
    window.open(`/v4/records/${collectionId}/${id}`, '_blank');
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* Eligible Complaints Card */}
      <ActionCard
        title="Eligible Complaints"
        total={eligibleComplaints.total}
        delta={eligibleComplaints.delta}
        trend={eligibleComplaints.trend}
      >
        {eligibleComplaints.topRecords.length > 0 ? (
          eligibleComplaints.topRecords.map((record) => (
            <RecordRow
              key={record.id}
              record={record}
              onClick={handleRecordClick}
              showConductScore
            />
          ))
        ) : (
          <EmptyState message="No eligible complaints in the last 24 hours" />
        )}
      </ActionCard>

      {/* Vulnerability Detector Card */}
      <ActionCard
        title="Vulnerability Detector"
        total={vulnerabilityDetector.total}
        delta={vulnerabilityDetector.delta}
        trend={vulnerabilityDetector.trend}
      >
        {vulnerabilityDetector.topRecords.length > 0 ? (
          vulnerabilityDetector.topRecords.map((record) => (
            <VulnerabilityRecordRow key={record.id} record={record} onClick={handleRecordClick} />
          ))
        ) : (
          <EmptyState message="No vulnerability cases in the last 24 hours" />
        )}
      </ActionCard>
    </div>
  );
}

/**
 * Empty state component for cards with no records.
 */
function EmptyState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex items-center justify-center px-6 py-8 text-[12px] text-muted-foreground">
      {message}
    </div>
  );
}
